/**
 * 주소 정하기 창 — 사업장·위치(위치 줄·기본 주소)와 모니터링 [사업장 구성](서버 기본 주소)이 연다.
 *
 * AssetOne 의 주소 입력과 같은 방식이다(2026-10-22 사용자 지시 "assetone 에서 사용한 주소 기능도 적용"). 기본 주소는
 * **서버의 주소 DB 에서 검색해 고르고**(행정안전부 도로명주소 — 인터넷 없이), 상세 주소(동·층·호)만 손으로 적는다.
 * 고른 주소에 좌표(주출입구, 「위치정보요약DB」)가 있으면 함께 담아 모니터링 지도(연동된 서버의 통합 모니터링)에 그 자리로 놓는다.
 *
 * 검색되지 않는 주소(주소 DB 를 아직 넣지 않은 서버, 해외 등)는 [직접 입력] 으로 우편번호·기본 주소를 적는다. 그때는
 * 좌표가 없어 지도에 서지 않으므로, 아는 경우 위도·경도를 함께 적을 수 있다.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MapPin, Search } from 'lucide-react';
import type { AddressSearchResult, SignageOneClient, SiteAddress } from '../api/client.js';
import { useAsync, useDebounced } from '../hooks.js';
import { ErrorNote, Modal } from './ui.js';
import { hasAddress, pointOf } from '../sites.js';

export const EMPTY_ADDRESS: SiteAddress = {
  postal_code: '',
  address: '',
  address_detail: '',
  latitude: null,
  longitude: null,
};

/** 검색 결과 하나를 저장할 주소로 — 상세 주소는 비우고(다른 곳의 것이다), 좌표는 있으면 담는다. */
export function addressFromResult(result: AddressSearchResult): SiteAddress {
  return {
    postal_code: result.postal_code,
    address: result.address,
    address_detail: '',
    latitude: typeof result.latitude === 'number' ? result.latitude : null,
    longitude: typeof result.longitude === 'number' ? result.longitude : null,
  };
}

/** 직접 적은 위도·경도 글자 → 숫자. 비었거나 범위 밖이면 null. */
export function coordinateOf(text: string, limit: number): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && Math.abs(value) <= limit ? value : null;
}

export function AddressDialog({
  client,
  title,
  value,
  inherited,
  onApply,
  onClose,
}: {
  client: SignageOneClient;
  title: string;
  value: SiteAddress | null;
  /** 비워 두면 따를 주소(위치 줄이면 이 서버의 기본 주소) — 안내에만 쓴다. */
  inherited?: string;
  /** 정한 주소 — null 이면 지웠다(기본 주소를 따른다). */
  onApply: (next: SiteAddress | null) => void;
  onClose: () => void;
}): ReactNode {
  const [draft, setDraft] = useState<SiteAddress>(value ?? EMPTY_ADDRESS);
  const [manual, setManual] = useState(false);
  const [query, setQuery] = useState('');
  const [lat, setLat] = useState(value?.latitude?.toString() ?? '');
  const [lng, setLng] = useState(value?.longitude?.toString() ?? '');
  const debounced = useDebounced(query.trim(), 250);
  const status = useAsync(() => client.addressDbStatus(), [client]);
  const found = useAsync(
    () => (debounced.length >= 2 ? client.searchAddress(debounced, 20) : Promise.resolve(null)),
    [client, debounced],
  );
  const detailRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => searchRef.current?.focus(), []);

  const loaded = status.data?.loaded ?? null;
  const results = found.data?.results ?? [];
  const pick = (result: AddressSearchResult): void => {
    const next = addressFromResult(result);
    // 같은 주소를 다시 골랐으면 적어 둔 상세 주소는 남긴다.
    setDraft({ ...next, address_detail: next.address === draft.address ? draft.address_detail : '' });
    setLat(next.latitude?.toString() ?? '');
    setLng(next.longitude?.toString() ?? '');
    setManual(false);
    setTimeout(() => detailRef.current?.focus(), 0);
  };

  const result: SiteAddress = manual
    ? { ...draft, latitude: coordinateOf(lat, 90), longitude: coordinateOf(lng, 180) }
    : draft;
  const point = pointOf(result);

  return (
    <Modal title={title} onClose={onClose}>
      <div className="flex flex-col gap-3">
        {status.error ? <ErrorNote message={status.error} /> : null}
        {loaded === false ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
            이 서버에는 아직 주소 DB 가 없습니다 — [시스템] 의 <strong>주소 DB</strong> 에서 행정안전부 주소 자료를
            넣으면 검색해서 고를 수 있습니다. 그전에는 [직접 입력] 으로 적으세요.
          </p>
        ) : null}

        {!manual ? (
          <div className="flex flex-col gap-2">
            <label className="field">
              <span>주소 검색</span>
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  ref={searchRef}
                  className="input w-full pl-8"
                  placeholder="도로명·건물명·지번·우편번호 (예: 평택항로 1)"
                  value={query}
                  disabled={loaded === false}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && results[0]) {
                      event.preventDefault();
                      pick(results[0]);
                    }
                  }}
                />
              </span>
            </label>
            {found.error ? <ErrorNote message={found.error} /> : null}
            {debounced.length >= 2 && found.data ? (
              results.length === 0 ? (
                <p className="hint">
                  {found.data.hint ?? '찾은 주소가 없습니다 — 도로명과 건물번호(예: 평택항로 1)로 찾아 보세요.'}
                </p>
              ) : (
                <ul className="address-results" role="listbox" aria-label="찾은 주소">
                  {results.map((item) => (
                    <li key={item.id}>
                      <button type="button" className="address-result" onClick={() => pick(item)}>
                        <span className="address-result-road">
                          {item.road_address}
                          {item.building_name ? <span className="text-slate-500"> · {item.building_name}</span> : null}
                        </span>
                        <span className="address-result-sub">
                          <span className="font-mono">{item.postal_code}</span> · 지번 {item.jibun_address}
                          {typeof item.latitude === 'number' ? null : (
                            <span className="text-amber-700"> · 좌표 없음</span>
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                  {found.data.more ? (
                    <li className="hint px-2 py-1">더 있습니다 — 건물번호나 동 이름을 더 적으세요.</li>
                  ) : null}
                </ul>
              )
            ) : null}
            {found.data?.relaxed ? <p className="hint">뒤에 적은 상세 주소를 빼고 찾았습니다.</p> : null}
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-[8rem_1fr]">
            <label className="field">
              <span>우편번호</span>
              <input
                className="input font-mono"
                maxLength={10}
                value={draft.postal_code}
                onChange={(event) => setDraft({ ...draft, postal_code: event.target.value })}
              />
            </label>
            <label className="field">
              <span>기본 주소 (직접 입력)</span>
              <input
                className="input"
                maxLength={200}
                value={draft.address}
                onChange={(event) => setDraft({ ...draft, address: event.target.value })}
              />
            </label>
            <label className="field">
              <span>위도 (선택)</span>
              <input
                className="input font-mono"
                inputMode="decimal"
                placeholder="37.5665"
                value={lat}
                onChange={(event) => setLat(event.target.value)}
              />
            </label>
            <label className="field">
              <span>경도 (선택)</span>
              <input
                className="input font-mono"
                inputMode="decimal"
                placeholder="126.9780"
                value={lng}
                onChange={(event) => setLng(event.target.value)}
              />
            </label>
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
          {hasAddress(result) ? (
            <p className="text-[13px] text-slate-800">
              {result.postal_code ? (
                <span className="mr-1.5 font-mono text-slate-500">{result.postal_code}</span>
              ) : null}
              {result.address}
            </p>
          ) : (
            <p className="hint">
              {inherited ? `비워 두면 기본 주소를 따릅니다 — ${inherited}` : '아직 고른 주소가 없습니다.'}
            </p>
          )}
          <p className={`mt-1 flex items-center gap-1 text-[12px] ${point ? 'text-emerald-700' : 'text-slate-500'}`}>
            <MapPin className="h-3 w-3" aria-hidden="true" />
            {point
              ? `지도 위치 ${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`
              : '좌표 없음 — 모니터링 지도에 표시되지 않습니다'}
          </p>
          <label className="field mt-2">
            <span>상세 주소 (동·층·호)</span>
            <input
              ref={detailRef}
              className="input"
              maxLength={100}
              value={draft.address_detail}
              onChange={(event) => setDraft({ ...draft, address_detail: event.target.value })}
            />
          </label>
        </div>

        <button type="button" className="link self-start text-[12.5px]" onClick={() => setManual((on) => !on)}>
          {manual ? '주소 검색으로 고르기' : '검색되지 않는 주소는 직접 입력'}
        </button>

        <div className="form-actions">
          {value && hasAddress(value) ? (
            <button
              type="button"
              className="btn mr-auto"
              onClick={() => {
                onApply(null);
                onClose();
              }}
            >
              주소 지우기
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!hasAddress(result)}
            onClick={() => {
              onApply(result);
              onClose();
            }}
          >
            적용
          </button>
        </div>
      </div>
    </Modal>
  );
}
